/**
 * The approval chain a new limit would get, shown in the cell edit's dialog before sending (masterplan
 * credit-limits-v3, B4; E3, E5, E20, E23, E34). Pure, computed from `GET /credit-limits/config` (tiers, approver rows,
 * Finance, `defaultTopEmail`).
 *
 * A PREVIEW: the binding chain is the one the API answers with (`workflow.ts` `createRequest`, which also takes the
 * snapshot of E18). This file mirrors `resolveTier` and `buildSteps` of `api/src/functions/credit-limits/shared/
 * workflow.ts` (B1, read 2026-10-23) plus the E34 rule of B1a for a customer without an active approver row:
 *
 *   E3   The tier follows the NEW limit (also when it is lowered), CHF, bounds inclusive.
 *   E20  A new limit <= 1 ("no limit") always needs BTM + TOP.
 *   E23  A requester with the competence approves at once (SM in tier 1, the row's BTM in tiers 1 and 2); in tier 3 a
 *        requesting BTM counts as the BTM approval and TOP is still needed. Otherwise the requester never counts: when
 *        he is the person asked for, the step moves up the ladder SM -> BTM -> TOP, last to Finance.
 *   E5   Finance never approves its own request: the other Finance person does.
 *   E34  No active row: tiers 1 and 2 one Finance step; tier 3 Finance in place of the BTM plus the default top level,
 *        two different people (the top level is reserved first; a requester who is the top level is refused).
 */
import type {
    CreditLimitApprover, CreditLimitFinanceMember, CreditLimitTier, RequiredRoles, StepRole,
} from '../../../types/creditLimits';
import { displayNameOf } from '../config/displayNames';

/**
 * E34: the chain uses `config.settings.defaultTopEmail` (sent since B5). Only when it is missing or blank this default
 * applies, which is what the server does too (`config-repo.ts` / `request-repo.ts`: missing or blank -> Crippa).
 */
export const DEFAULT_TOP_EMAIL = 'bruno.crippa@dolder.com';
/** A limit <= 1 means "no limit" (masterplan §2). */
export const NO_LIMIT_MAX_CHF = 1;

export const ROLE_LABEL: Record<StepRole, string> = {
    SM: 'salesperson',
    BTM: 'BT manager',
    TOP: 'top level',
    FIN: 'finance',
};

export interface PreviewStep {
    role: StepRole;
    email: string;
    /** Given at once by the requester's own competence (E23). */
    approved: boolean;
}

export type ChainPreview =
    | {
        ok: true;
        tierNo: number;
        requiredRoles: RequiredRoles;
        noLimit: boolean;
        steps: PreviewStep[];
        /** Every step is the requester's own: the request is «Ready for NAV» at once. */
        readyAtOnce: boolean;
    }
    | { ok: false; error: string };

export interface ChainPreviewInput {
    requesterEmail: string;
    requestedLimitChf: number;
    salesManagerName: string | null;
    tiers: readonly CreditLimitTier[];
    approvers: readonly CreditLimitApprover[];
    finance: readonly CreditLimitFinanceMember[];
    defaultTopEmail?: string | null;
}

export function normalizeEmail(email: string | null | undefined): string {
    return (email ?? '').trim().toLowerCase();
}

/** The customer's active approver row: exact name, trimmed, case-insensitive; never a substring (masterplan §2). */
export function findApproverRow(approvers: readonly CreditLimitApprover[], salesManagerName: string | null): CreditLimitApprover | null {
    const key = (salesManagerName ?? '').trim().toLowerCase();
    if (key === '') return null;
    return approvers.find(a => a.isActive && a.salesManagerName.trim().toLowerCase() === key) ?? null;
}

class PreviewError extends Error {}

function activeFinance(finance: readonly CreditLimitFinanceMember[]): string[] {
    return finance.filter(f => f.isActive).map(f => normalizeEmail(f.email)).filter(e => e !== '');
}

function pickFinance(used: Set<string>, finance: readonly CreditLimitFinanceMember[]): string {
    const email = activeFinance(finance).find(e => !used.has(e));
    if (!email) throw new PreviewError('No other active Finance person can decide this request.');
    used.add(email);
    return email;
}

const LADDER: readonly ('SM' | 'BTM' | 'TOP')[] = ['SM', 'BTM', 'TOP'];

function emailForRole(role: 'SM' | 'BTM' | 'TOP', row: CreditLimitApprover): string {
    return normalizeEmail(role === 'SM' ? row.smEmail : role === 'BTM' ? row.btmEmail : row.topEmail);
}

function resolveApprover(role: 'SM' | 'BTM' | 'TOP', row: CreditLimitApprover, used: Set<string>, finance: readonly CreditLimitFinanceMember[]): PreviewStep {
    for (const candidate of LADDER.slice(LADDER.indexOf(role))) {
        const email = emailForRole(candidate, row);
        if (email !== '' && !used.has(email)) {
            used.add(email);
            return { role: candidate, email, approved: false };
        }
    }
    return { role: 'FIN', email: pickFinance(used, finance), approved: false };
}

/** E3 / E20: the tier of a new limit; null when no tier covers it. */
export function resolveTier(requestedLimitChf: number, tiers: readonly CreditLimitTier[]): { tierNo: number; requiredRoles: RequiredRoles; noLimit: boolean } | null {
    const sorted = [...tiers].sort((a, b) => a.tierNo - b.tierNo);
    if (requestedLimitChf <= NO_LIMIT_MAX_CHF) {
        const top = sorted.find(t => t.requiredRoles === 'BTM+TOP') ?? sorted[sorted.length - 1];
        return top ? { tierNo: top.tierNo, requiredRoles: 'BTM+TOP', noLimit: true } : null;
    }
    const tier = sorted.find(t => t.maxAmountChf === null || requestedLimitChf <= t.maxAmountChf);
    return tier ? { tierNo: tier.tierNo, requiredRoles: tier.requiredRoles, noLimit: false } : null;
}

function buildSteps(requiredRoles: RequiredRoles, requester: string, row: CreditLimitApprover | null, input: ChainPreviewInput): PreviewStep[] {
    const used = new Set<string>([requester]);
    if (!row) {
        // E34: Finance decides; in tier 3 Finance in place of the BTM plus the default top level. As the server
        // (`workflow.ts` buildSteps): the top level is reserved first, so Finance is never the same person, and a
        // requester who is the top level himself gets the server's refusal (NO_TOP_APPROVER), not another chain.
        if (requiredRoles !== 'BTM+TOP') return [{ role: 'FIN', email: pickFinance(used, input.finance), approved: false }];
        const top = normalizeEmail(input.defaultTopEmail) || DEFAULT_TOP_EMAIL;
        if (top === requester) throw new PreviewError('You are the top level for customers without a salesperson: someone else must request this limit.');
        used.add(top);
        return [
            { role: 'FIN', email: pickFinance(used, input.finance), approved: false },
            { role: 'TOP', email: top, approved: false },
        ];
    }
    const isSm = normalizeEmail(row.smEmail) === requester;
    const isBtm = normalizeEmail(row.btmEmail) === requester;
    if (requiredRoles === 'SM' && (isSm || isBtm)) return [{ role: isSm ? 'SM' : 'BTM', email: requester, approved: true }];
    if (requiredRoles === 'BTM' && isBtm) return [{ role: 'BTM', email: requester, approved: true }];
    const chain: ('SM' | 'BTM' | 'TOP')[] = requiredRoles === 'SM' ? ['SM'] : requiredRoles === 'BTM' ? ['BTM'] : ['BTM', 'TOP'];
    return chain.map(role => (role === 'BTM' && isBtm
        ? { role: 'BTM', email: requester, approved: true }
        : resolveApprover(role, row, used, input.finance)));
}

/** The chain a new limit would get. Never throws: a gap in the configuration comes back as `ok: false`. */
export function previewChain(input: ChainPreviewInput): ChainPreview {
    const requester = normalizeEmail(input.requesterEmail);
    const tier = resolveTier(input.requestedLimitChf, input.tiers);
    if (!tier) return { ok: false, error: 'No approval tier covers this amount.' };
    try {
        const row = findApproverRow(input.approvers, input.salesManagerName);
        const steps = buildSteps(tier.requiredRoles, requester, row, input);
        return { ok: true, ...tier, steps, readyAtOnce: steps.every(s => s.approved) };
    } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'The approval chain cannot be computed.' };
    }
}

/**
 * The name of a person: the display name `GET /credit-limits/config` carries for the address (B5, from
 * `etl.EmployeeMaster`, kept in `config/displayNames.ts`); for an address it does not list, a readable label derived
 * from it («bruno.crippa@dolder.com» -> «Bruno Crippa», the names of the house follow «first.last»). The full address
 * stays in the tooltip.
 */
export function nameFromEmail(email: string | null | undefined): string {
    const known = displayNameOf(email);
    if (known) return known;
    const local = normalizeEmail(email).split('@')[0] ?? '';
    if (local === '') return '–';
    return local
        .split(/[._]+/)
        .filter(Boolean)
        .map(part => part.split('-').map(p => (p ? p[0]!.toUpperCase() + p.slice(1) : p)).join('-'))
        .join(' ');
}

/** One step in words: «Wolfgang Gomm (BT manager)», «you (BT manager, own competence)». */
export function stepText(step: PreviewStep, requesterEmail: string): string {
    const role = ROLE_LABEL[step.role];
    if (step.approved && step.email === normalizeEmail(requesterEmail)) return `you (${role}, own competence)`;
    return `${nameFromEmail(step.email)} (${role})`;
}

/** The preview in one line: «Approval by Wolfgang Gomm (BT manager), then Bruno Crippa (top level)». */
export function chainText(preview: ChainPreview, requesterEmail: string): string {
    if (!preview.ok) return preview.error;
    const own = preview.steps.filter(s => s.approved);
    const open = preview.steps.filter(s => !s.approved);
    if (open.length === 0) return `Approved at once by ${own.map(s => stepText(s, requesterEmail)).join(' and ')}: ready for NAV.`;
    const rest = open.map(s => stepText(s, requesterEmail)).join(', then ');
    if (own.length === 0) return `Approval by ${rest}.`;
    return `Counts as approved by ${own.map(s => stepText(s, requesterEmail)).join(', ')}; then approval by ${rest}.`;
}
